import { Button, Card, Center, Icon, Input, Label, Modal, Row, Separator, Stack, Text } from '@/ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Loader2, Mail, Sparkles } from 'lucide-react';
import { authService } from '../services';
import { SocialLoginButton } from './SocialLoginButton';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AuthModal = React.memo(function AuthModal({
  isOpen,
  onClose,
  onSuccess: _onSuccess
}: AuthModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(() => authService.getSavedEmail() || '');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSendMagicLink = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!email.trim()) {
      toast(t('ui.auth.emailRequired'), {
        description: t('ui.auth.enterEmailForMagicLink'),
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await authService.sendMagicLink({
        email: email.trim()
      });

      if (error) {
        throw error;
      }

      setEmailSent(true);

      toast.success(t('ui.auth.magicLinkSent'), {
        description: t('ui.auth.checkEmailAndClick'),
      });

    } catch (error) {
      // Get user-friendly error message
      let errorMessage = t('ui.auth.somethingWentWrong');
      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('firebase is not configured') || msg.includes('missing firebase')) {
          errorMessage = t('ui.auth.emailAuthUnavailable');
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = t('ui.auth.networkError');
        } else if (msg.includes('invalid email') || msg.includes('invalid-email')) {
          errorMessage = t('ui.auth.invalidEmail');
        } else {
          errorMessage = error.message;
        }
      }

      toast.error(t('ui.auth.couldNotSendMagicLink'), {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = (): void => {
    setEmailSent(false);
    setEmail('');
  };

  const handleClose = (): void => {
    setEmailSent(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleClose}
      title={emailSent ? t('ui.auth.checkYourEmail') : t('ui.auth.magicLinkLogin')}
      description={emailSent
        ? t('ui.auth.magicLinkSentDescription')
        : t('ui.auth.enterEmailDescription')
      }
      icon={<Icon icon={Sparkles} size="lg" color="primary" />}
      size="md"
    >
      <Stack gap={6}>
        {!emailSent ? (
          /* Email Input Form */
          <Stack gap={4}>
            {/* Social Login Options */}
            <Stack gap={3}>
              <SocialLoginButton provider="google" />
            </Stack>

            {/* Divider */}
            <Row align="center" gap={2}>
              <Separator className="flex-1" />
              <Text size="xs" variant="muted" className="uppercase">
                {t('ui.auth.orWithEmail')}
              </Text>
              <Separator className="flex-1" />
            </Row>

            {/* Email Magic Link Form */}
            <form onSubmit={(e) => {
              void handleSendMagicLink(e);
            }}>
              <Stack gap={4}>
                <Stack gap={2}>
                  <Label htmlFor="authEmail">
                    <Text size="sm" weight="medium">
                      {t('ui.auth.emailAddress')}
                    </Text>
                  </Label>
                  <Input
                    id="authEmail"
                    type="email"
                    variant="tangi"
                    leftIcon={<Icon icon={Mail} size="sm" />}
                    placeholder={t('ui.auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    inputSize="lg"
                    required
                    autoFocus
                    autoComplete="email"
                    autoCapitalize="none"
                    inputMode="email"
                  />
                </Stack>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Icon icon={Loader2} size="sm" animate="spin" />
                      {t('ui.auth.sendingMagicLink')}
                    </>
                  ) : (
                    <>
                      <Icon icon={Sparkles} size="sm" />
                      {t('ui.auth.sendMagicLink')}
                    </>
                  )}
                </Button>
              </Stack>
            </form>
          </Stack>
        ) : (
          /* Email Sent Confirmation */
          <Stack gap={6} align="center">
            <Center style={{
              width: '5rem',
              height: '5rem',
              backgroundColor: 'hsl(var(--success) / 0.2)',
              borderRadius: '9999px',
              animation: 'bounce 1s infinite',
              boxShadow: 'var(--shadow-sm)'
            }}>
              <Icon icon={CheckCircle} size="xl" color="success" />
            </Center>

            <Stack gap={2} align="center">
              <Text size="lg" weight="semibold" variant="default">
                {t('ui.auth.magicLinkSentTitle')}
              </Text>
              <Text size="sm" variant="muted">
                {t('ui.auth.sentSecureLinkTo')}
              </Text>
              <Card variant="subtle" padding="sm" style={{
                backgroundColor: 'hsl(var(--accent) / 0.05)',
                borderColor: 'hsl(var(--accent) / 0.2)'
              }}>
                <Text weight="medium" variant="accent">{email}</Text>
              </Card>
            </Stack>

            <Stack gap={3}>
              <Button
                onClick={handleReset}
                variant="ghost"
                size="sm"
              >
                <Icon icon={ArrowLeft} size="sm" />
                {t('ui.auth.useDifferentEmail')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
});
